import { Link } from "react-router-dom";
import { formatAud, BACKEND_URL } from "../lib/api";
import { useCart }   from "../context/CartContext";
import { Button }    from "./ui/button";
import { Plus, Check } from "lucide-react";
import { useState } from "react";

// Proxy Pinnacle images through backend (requires auth)
const proxyImg = (url) => {
  if (!url) return "";
  if (url.startsWith("http://api.carparts-au.com/")) {
    return `${BACKEND_URL}/api/proxy/image?url=${encodeURIComponent(url)}`;
  }
  return url;
};

const TAG_STYLES = {
  "New Arrival": "bg-blue-500/90 text-white",
  "Clearance":   "bg-[#e31e24]/90 text-white",
  "Hot Deal":    "bg-orange-500/90 text-white",
  "Last One":    "bg-purple-600/90 text-white",
  "Low Stock":   "bg-yellow-500/90 text-[#0b1a3b]",
  "Featured":    "bg-green-600/90 text-white",
};

function TagChips({ tags }) {
  if (!tags || tags.length === 0) return null;
  return (
    <div className="flex flex-wrap gap-1 mt-2">
      {tags.slice(0, 4).map((tag) => (
        <span key={tag}
          className={`text-[9px] font-mono font-bold uppercase tracking-widest px-1.5 py-0.5 rounded-sm ${TAG_STYLES[tag] || "bg-[#0b1a3b]/80 text-white"}`}>
          {tag}
        </span>
      ))}
    </div>
  );
}

export default function ProductCard({ product }) {
  const { addItem } = useCart();
  const [added, setAdded] = useState(false);

  const onAdd = (e) => {
    e.preventDefault();
    addItem(product, 1);
    setAdded(true);
    setTimeout(() => setAdded(false), 1200);
  };

  const inStock              = (product.stock ?? 0) > 0;
  const isBackorderAvailable = !inStock && product.backorder_available;
  const canAdd               = inStock || isBackorderAvailable;
  const pct = Number(product.discount_pct || 0);
  const rrp = pct > 0 ? product.price / (1 - pct / 100) : null;

  return (
    <Link to={`/product/${product.id}`} data-testid={`product-card-${product.sku}`}
      className="group flex flex-col border border-zinc-200 hover:border-[#1e5bb8] bg-white rounded-sm overflow-hidden transition-all duration-300 shadow-sm hover:shadow-xl hover:-translate-y-1">

      <div className="relative aspect-square bg-zinc-100 overflow-hidden">
        {(product.featured_image || product.image_url) ? (
          <img src={proxyImg(product.featured_image || product.image_url)} alt={product.name}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
        ) : (
          <div className="w-full h-full flex items-center justify-center font-mono text-xs text-zinc-400 uppercase tracking-widest">No image</div>
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-[#0b1a3b]/70 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end p-3">
          <div className="text-white font-mono text-[10px] uppercase tracking-widest flex items-center gap-1">
            View details <span className="transition-transform duration-300 group-hover:translate-x-1">→</span>
          </div>
        </div>

        {/* Featured badge — top left (SKU removed) */}
        {product.is_featured && (
          <div className="absolute top-3 left-3">
            <div className="font-mono text-[10px] bg-yellow-400 text-[#0b1a3b] px-2 py-1 uppercase tracking-widest rounded-sm font-bold">★ Featured</div>
          </div>
        )}

        {/* Discount badge — top right */}
        {product.discount_pct > 0 && (
          <div className="absolute top-3 right-3 font-mono text-[11px] bg-[#e31e24] text-white px-2 py-1 uppercase tracking-widest rounded-sm font-bold">
            -{product.discount_pct}%
          </div>
        )}
        {!inStock && !product.discount_pct && !isBackorderAvailable && (
          <div className="absolute top-3 right-3 font-mono text-[10px] bg-zinc-600 text-white px-2 py-1 uppercase tracking-widest rounded-sm">Sold</div>
        )}
        {isBackorderAvailable && (
          <div className="absolute top-3 right-3 font-mono text-[10px] bg-amber-500 text-white px-2 py-1 uppercase tracking-widest rounded-sm">Backorder</div>
        )}
      </div>

      <div className="flex-1 p-4 flex flex-col">
        <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-[#1e5bb8] mb-1">{product.brand} · {product.category}</div>
        <div className="font-heading font-bold text-sm leading-snug text-[#0b1a3b] line-clamp-2 min-h-[40px]">{product.name}</div>
        {(product.vehicle_make || product.vehicle_year) && (
          <div className="mt-1 text-xs text-zinc-500">
            {product.vehicle_year && <span className="font-mono mr-1">{product.vehicle_year}</span>}
            {product.vehicle_make} {product.vehicle_model}
          </div>
        )}

        <TagChips tags={product.tags} />

        <div className="mt-4 flex items-end justify-between gap-3">
          <div>
            <div className="font-heading text-xl font-bold text-[#0b1a3b]">{formatAud(product.price)}</div>
            {rrp && (
              <div className="flex items-center gap-2 mt-0.5">
                <span className="text-[11px] text-zinc-400 line-through font-mono">RRP {formatAud(rrp)}</span>
                <span className="text-[10px] font-mono uppercase tracking-widest text-[#e31e24] font-bold">{pct}% OFF</span>
              </div>
            )}
            <div className={`text-[11px] font-mono uppercase tracking-widest mt-0.5 ${inStock ? "text-green-600" : isBackorderAvailable ? "text-amber-600" : "text-zinc-400"}`}>
              {inStock ? `In stock · ${product.stock}` : isBackorderAvailable ? "Backorder available" : "Out of stock"}
            </div>
          </div>
          <Button onClick={onAdd} disabled={!canAdd} data-testid={`product-add-${product.sku}`}
            className={`h-10 rounded-sm text-white font-bold uppercase tracking-wide text-xs px-3 shrink-0 ${!inStock && isBackorderAvailable ? "bg-amber-500 hover:bg-amber-600" : "bcp-gradient-red hover:opacity-90"}`}>
            {added ? <><Check className="h-4 w-4 mr-1" /> Added</> : !inStock && isBackorderAvailable ? <>Backorder</> : <><Plus className="h-4 w-4 mr-1" /> Add</>}
          </Button>
        </div>
      </div>
    </Link>
  );
}